// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import type { FormProps } from 'redux-form';

import type {
  Catalog,
  CatalogType,
  CreationCatalog,
  Dispatch,
  GlobalSettings,
} from 'state/types';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import { createCatalog } from 'state/commands/catalogCommands';
import DialogPanel from 'view/App/DialogPanels/DialogPanel';
import localizedStrings from 'util/localizedStrings';
import {
  CATALOG_STORAGE_TYPE,
  CREATE_CATALOG_PANEL_ID,
  DEFAULT_CATALOG_TYPE_NAMES,
} from 'util/constants';
import { hideDialogPanel } from 'state/commands/dialogPanelCommands';
import {
  getActivePanelId,
  getCatalogs,
  getCatalogTypes,
  getCreateAutosyncFolder,
  getGlobalSettings,
  getLocale,
} from 'state/selectors';
import { catalogNameExists, isLegalCatalogName } from 'util/catalogUtils';
import CheckRadio from 'view/Forms/BulmaCheckRadio';
import logger from 'portfolio-shared/util/logging';

const msgs = require('../../../locale/master-strings.json');

type Props = {
  activePanelId: ?string,
  catalogs: Catalog[],
  catalogTypes: CatalogType[],
  createCatalog: typeof createCatalog,
  globalSettings: GlobalSettings,
  hideDialogPanel: typeof hideDialogPanel,
  intl: IntlShape,
  storageType: string,
} & FormProps;

function validate(data: Object, props: Props) {
  const errors = {};
  if (!data.name || !data.name.trim()) {
    errors.name = 'Required';
  } else if (catalogNameExists(props.catalogs, data.name)) {
    errors.name = 'Duplicate';
  }

  if (!data.storageType) {
    errors.storageType = 'Required';
  }
  return errors;
}

const collations = [
  { value: 'en', label: 'english' },
  { value: 'fr', label: 'french' },
  { value: 'de', label: 'german' },
  { value: 'ja', label: 'japanese' },
  { value: 'sv', label: 'swedish' },
];

class CreateCatalogDialog extends Component<Props> {
  handleClose: Function;
  isOkEnabled: Function;
  prepareCatalogTypes: Function;
  submit: Function;

  constructor() {
    super();

    this.handleClose = this.handleClose.bind(this);
    this.isOkEnabled = this.isOkEnabled.bind(this);
    this.prepareCatalogTypes = this.prepareCatalogTypes.bind(this);
    this.submit = this.submit.bind(this);
  }

  isOkEnabled() {
    return this.props.valid && !this.props.pristine;
  }

  handleClose() {
    this.props.reset();
  }

  handleChange(event, newValue, previousValue, name) {
    if (!isLegalCatalogName(newValue)) {
      event.preventDefault();
    }
  }

  submit(values) {
    const { catalogTypes, createCatalog, hideDialogPanel, intl } = this.props;
    const {
      collation,
      createAutosyncFolder,
      name,
      storageType,
      template,
      useVersioning,
    } = values;
    // if we are using a custom template then set the locale to custom as well
    const templateObject = catalogTypes.find(t => t.name === template);
    if (!templateObject) {
      logger.warn('Could not find template for name ' + template);
      return;
    }
    const isCustom: boolean = !!templateObject.guid;
    const catalogTypeSpecifier = isCustom
      ? templateObject.guid
      : templateObject.name;
    const catalogTypeLocale = isCustom ? 'custom' : intl.locale;

    const watchFolderNamePartFiles = intl.formatMessage(
      localizedStrings.FOLDER_PATH_AUTOSYNC_FILES_STRING
    );
    const catalog: CreationCatalog = {
      catalogType: catalogTypeSpecifier,
      catalogTypeLocale,
      collation,
      catalogId: '',
      isVersioningEnabled:
        useVersioning && storageType === CATALOG_STORAGE_TYPE.VAULT,
      name: name.trim(),
      storageType,
    };
    hideDialogPanel();
    createCatalog(catalog, createAutosyncFolder, watchFolderNamePartFiles);
  }

  prepareCatalogTypes(types) {
    const builtIns = [];
    const customs = [];
    types.forEach(type => {
      if (type.guid && type.guid.length > 0) {
        customs.push(type);
      } else {
        builtIns.push(type);
      }
    });
    return [
      builtIns.sort((a, b) => {
        if (DEFAULT_CATALOG_TYPE_NAMES.includes(a.name)) {
          return -1;
        } else if (DEFAULT_CATALOG_TYPE_NAMES.includes(b.name)) {
          return 1;
        }
        return a.name.localeCompare(b.name);
      }),
      customs.sort((a, b) => a.name.localeCompare(b.name)),
    ];
  }

  renderCatalogTypes(catalogTypes) {
    const [builtIns, customs] = this.prepareCatalogTypes(catalogTypes);
    return [
      builtIns.map(opt => this.renderCatalogType(opt)),
      customs && customs.length > 0 && (
        <option key="sep" disabled="disabled">
          ─────────────────────────
        </option>
      ),
      customs.map(opt => this.renderCatalogType(opt)),
    ];
  }

  renderCatalogType(type) {
    return (
      <option key={type.name} value={type.name}>
        {type.name}
      </option>
    );
  }

  render() {
    const {
      activePanelId,
      handleSubmit,
      hideDialogPanel,
      intl: { formatMessage },
    } = this.props;
    return (
      <DialogPanel
        activePanelId={activePanelId}
        contentStyle={{ width: '650px', height: null }}
        customButtonText={formatMessage(localizedStrings.CREATE)}
        dialogFocusId="name"
        hideDialogPanel={hideDialogPanel}
        isOkEnabled={this.isOkEnabled}
        onClose={this.handleClose}
        onSave={handleSubmit(this.submit)}
        panelId={CREATE_CATALOG_PANEL_ID}
        panelTitle={formatMessage(localizedStrings.CREATE_CATALOG)}
        showCancel
        dataTestName="create-catalog-dialog"
      >
        {this.renderForm()}
      </DialogPanel>
    );
  }

  renderForm() {
    const {
      catalogTypes,
      handleSubmit,
      intl: { formatMessage },
      storageType,
    } = this.props;
    return (
      <form
        className="portfolio-form-container"
        onSubmit={handleSubmit(this.submit)}
      >
        <div className="field is-horizontal" key="name">
          <div className="field-label is-normal">
            <label className="label">
              <FormattedMessage {...localizedStrings.NAME} />
            </label>
          </div>
          <div className="field-body">
            <div className="field">
              <div className="control">
                <Field
                  className="input"
                  component="input"
                  id="name"
                  name="name"
                  onChange={this.handleChange}
                  type="text"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="vertical-spacer-large" />

        <div className="field is-horizontal" key="template">
          <div className="field-label is-normal">
            <label className="label">
              <FormattedMessage
                {...localizedStrings.CATALOG_CREATE_CATALOG_TEMPLATE}
              />
            </label>
          </div>
          <div className="field-body">
            <div className="field">
              <div className="control is-expanded">
                <div className="select is-fullwidth">
                  <Field
                    className="select is-fullwidth"
                    component="select"
                    id="template"
                    name="template"
                  >
                    {this.renderCatalogTypes(catalogTypes)}
                  </Field>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="field is-horizontal" key="locale">
          <div className="field-label is-normal">
            <label className="label">
              <FormattedMessage
                {...localizedStrings.CATALOG_DETAILS_COLLATION}
              />
            </label>
          </div>
          <div className="field-body">
            <div className="field">
              <div className="control is-expanded">
                <div className="select is-fullwidth">
                  <Field
                    className="select is-fullwidth"
                    component="select"
                    id="collation"
                    name="collation"
                  >
                    {collations.map(collation => (
                      <option key={collation.value} value={collation.value}>
                        {formatMessage(localizedStrings[collation.label])}
                      </option>
                    ))}
                  </Field>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="vertical-spacer-large" />

        <div className="field is-horizontal" key="storageType">
          <div className="field-label is-normal">
            <label className="label">
              <FormattedMessage
                {...localizedStrings.CATALOG_STORAGE_TYPE_LABEL}
              />
            </label>
          </div>
          <div className="field-body">
            <div className="control">
              <div className="field" style={{ marginTop: '6px' }}>
                <Field
                  component={CheckRadio}
                  fieldValue={storageType}
                  id="storageType-vault"
                  label={formatMessage(
                    localizedStrings.CATALOG_STORAGE_TYPE_VAULT
                  )}
                  name="storageType"
                  type="radio"
                  value={CATALOG_STORAGE_TYPE.VAULT}
                />
                <p className="help is-indented">
                  <FormattedMessage
                    {...localizedStrings.CATALOG_EXPLANATION_VAULT_1}
                  />
                </p>
                <p className="help is-indented">
                  <FormattedMessage
                    {...localizedStrings.CATALOG_EXPLANATION_VAULT_2}
                  />
                </p>
                <div className="vertical-spacer-small" />
                <div className="is-indented">
                  <Field
                    className="is-small"
                    component={CheckRadio}
                    disabled={storageType !== CATALOG_STORAGE_TYPE.VAULT}
                    id="useVersioning"
                    label={formatMessage(
                      localizedStrings.CATALOG_CREATE_USE_VERSIONING
                    )}
                    name="useVersioning"
                    type="checkbox"
                  />
                </div>
              </div>

              <div className="vertical-spacer-large" />

              <div className="field">
                <Field
                  component={CheckRadio}
                  fieldValue={storageType}
                  id="storageType-watchFolders"
                  label={formatMessage(
                    localizedStrings.CATALOG_STORAGE_TYPE_WATCHFOLDERS
                  )}
                  name="storageType"
                  type="radio"
                  value={CATALOG_STORAGE_TYPE.FILESYSTEM}
                />
                <p className="help is-indented">
                  <FormattedMessage
                    {...localizedStrings.CATALOG_EXPLANATION_WF_1}
                  />
                </p>
                <p className="help is-indented">
                  <FormattedMessage
                    {...localizedStrings.CATALOG_EXPLANATION_WF_2}
                  />
                </p>
                <div className="vertical-spacer-small" />
                <div className="is-indented">
                  <Field
                    className="is-small"
                    component={CheckRadio}
                    disabled={storageType !== CATALOG_STORAGE_TYPE.FILESYSTEM}
                    id="createAutosyncFolder"
                    label={formatMessage(
                      localizedStrings.CATALOG_CREATE_AUTOSYNC_FOLDER
                    )}
                    name="createAutosyncFolder"
                    type="checkbox"
                  />
                </div>
              </div>

              <div className="vertical-spacer-large" />

              <p className="help">
                <FormattedMessage
                  {...localizedStrings.CATALOG_CREATE_GLOBAL_SETTING_INFO_MSG}
                />
              </p>

              <div className="vertical-spacer-large" />
            </div>
          </div>
        </div>
      </form>
    );
  }
}

const form = reduxForm({
  form: CREATE_CATALOG_PANEL_ID,
  validate,
})(injectIntl(CreateCatalogDialog));

const selector = formValueSelector(CREATE_CATALOG_PANEL_ID);
export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    catalogs: getCatalogs(state),
    catalogTypes: getCatalogTypes(state),
    globalSettings: getGlobalSettings(state),
    storageType: selector(state, 'storageType'),
    initialValues: {
      collation: 'en',
      createAutosyncFolder: getCreateAutosyncFolder(state),
      useVersioning: true,
      // This is a bit of a hack, we need to find a template via a localized name
      template: msgs['CATALOG_TYPE_GENERAL'][getLocale(state)] || 'General Use',
    },
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      createCatalog,
      hideDialogPanel,
    })
)(form);



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/CreateCatalogDialog.js